import { TaxiStand } from '../api/taxiStands';

interface OfflineData {
    taxiStands: TaxiStand[];
    recentSearches: any[];
    lastSync: number;
    version: string;
}

class OfflineManager {
    private db: IDBDatabase | null = null;
    private dbName = 'TaxiAppOfflineDB';
    private dbVersion = 1;
    private isOnline: boolean = navigator.onLine;

    constructor() {
        this.init();
        this.setupOnlineOfflineListeners();
    }

    private async init(): Promise<void> {
        if (typeof window === 'undefined' || !('indexedDB' in window)) {
            console.warn('IndexedDB not supported, offline features disabled');
            return;
        }

        try {
            this.db = await this.openDatabase();
            console.log('📱 Offline database initialized');
        } catch (error) {
            console.error('Failed to initialize offline database:', error);
        }
    }

    private openDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                
                // Create taxi stands store
                if (!db.objectStoreNames.contains('taxiStands')) {
                    const taxiStandsStore = db.createObjectStore('taxiStands', { keyPath: 'id' });
                    taxiStandsStore.createIndex('location', ['location.latitude', 'location.longitude'], { unique: false });
                    taxiStandsStore.createIndex('district', 'district.en', { unique: false });
                }

                // Create recent searches store
                if (!db.objectStoreNames.contains('recentSearches')) {
                    const recentSearchesStore = db.createObjectStore('recentSearches', { keyPath: 'id' });
                    recentSearchesStore.createIndex('timestamp', 'timestamp', { unique: false });
                }

                // Create app data store
                if (!db.objectStoreNames.contains('appData')) {
                    db.createObjectStore('appData', { keyPath: 'key' });
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    private setupOnlineOfflineListeners(): void {
        if (typeof window === 'undefined') return;

        window.addEventListener('online', () => {
            this.isOnline = true;
            console.log('🌐 App is online');
            this.syncPendingData();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            console.log('📱 App is offline');
        });
    }

    // Taxi stands offline methods
    async saveTaxiStands(stands: TaxiStand[]): Promise<void> {
        if (!this.db) return;

        const transaction = this.db.transaction(['taxiStands'], 'readwrite');
        const store = transaction.objectStore('taxiStands');

        // Clear existing data
        await this.clearStore('taxiStands');

        // Save new data
        for (const stand of stands) {
            store.add(stand);
        }

        // Update last sync time
        await this.setAppData('lastTaxiStandsSync', Date.now());
        
        console.log(`💾 Saved ${stands.length} taxi stands offline`);
    }

    async getTaxiStands(): Promise<TaxiStand[]> {
        if (!this.db) return [];

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['taxiStands'], 'readonly');
            const store = transaction.objectStore('taxiStands');
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getTaxiStandsByLocation(lat: number, lon: number, radius: number = 5000): Promise<TaxiStand[]> {
        if (!this.db) return [];

        const allStands = await this.getTaxiStands();
        
        // Filter by distance (simple implementation)
        return allStands.filter(stand => {
            const distance = this.calculateDistance(
                lat, lon, 
                stand.location.latitude, 
                stand.location.longitude
            );
            return distance <= radius;
        });
    }

    // Recent searches offline methods
    async saveRecentSearches(searches: any[]): Promise<void> {
        if (!this.db) return;

        const transaction = this.db.transaction(['recentSearches'], 'readwrite');
        const store = transaction.objectStore('recentSearches');

        // Clear existing data
        await this.clearStore('recentSearches');

        // Save new data
        for (const search of searches) {
            store.add(search);
        }

        console.log(`💾 Saved ${searches.length} recent searches offline`);
    }

    async getRecentSearches(): Promise<any[]> {
        if (!this.db) return [];

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['recentSearches'], 'readonly');
            const store = transaction.objectStore('recentSearches');
            const index = store.index('timestamp');
            const request = index.getAll();

            request.onsuccess = () => {
                // Sort by timestamp descending (most recent first)
                const results = request.result.sort((a, b) => b.timestamp - a.timestamp);
                resolve(results);
            };
            request.onerror = () => reject(request.error);
        });
    }

    // App data methods
    async setAppData(key: string, value: any): Promise<void> {
        if (!this.db) return;

        const transaction = this.db.transaction(['appData'], 'readwrite');
        const store = transaction.objectStore('appData');
        store.put({ key, value, timestamp: Date.now() });
    }

    async getAppData(key: string): Promise<any> {
        if (!this.db) return null;

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['appData'], 'readonly');
            const store = transaction.objectStore('appData');
            const request = store.get(key);

            request.onsuccess = () => resolve(request.result?.value || null);
            request.onerror = () => reject(request.error);
        });
    }

    // Utility methods
    private async clearStore(storeName: string): Promise<void> {
        if (!this.db) return;

        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        store.clear();
    }

    private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371e3; // Earth's radius in meters
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ/2) * Math.sin(Δλ/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

        return R * c;
    }

    private async syncPendingData(): Promise<void> {
        if (!this.isOnline) return;

        try {
            // Sync recent searches
            const recentSearches = await this.getRecentSearches();
            if (recentSearches.length > 0) {
                console.log('🔄 Syncing recent searches...');
                // Here you would sync with your backend
            }

            // Sync taxi stands
            const lastSync = await this.getAppData('lastTaxiStandsSync');
            const now = Date.now();
            const syncAge = now - (lastSync || 0);
            
            if (syncAge > 300000) { // 5 minutes
                console.log('🔄 Taxi stands data is stale, refreshing...');
                // Here you would refresh taxi stands data
            }

        } catch (error) {
            console.error('Failed to sync pending data:', error);
        }
    }

    // Public API
    isAppOnline(): boolean {
        return this.isOnline;
    }

    async getOfflineData(): Promise<OfflineData> {
        const [taxiStands, recentSearches, lastSync] = await Promise.all([
            this.getTaxiStands(),
            this.getRecentSearches(),
            this.getAppData('lastSync')
        ]);

        return {
            taxiStands,
            recentSearches,
            lastSync: lastSync || 0,
            version: this.dbVersion.toString()
        };
    }

    async clearAllData(): Promise<void> {
        if (!this.db) return;

        const storeNames = ['taxiStands', 'recentSearches', 'appData'];
        const transaction = this.db.transaction(storeNames, 'readwrite');
        
        for (const storeName of storeNames) {
            const store = transaction.objectStore(storeName);
            store.clear();
        }

        console.log('🧹 All offline data cleared');
    }

    async getStorageInfo(): Promise<{
        taxiStandsCount: number;
        recentSearchesCount: number;
        lastSync: number;
        isOnline: boolean;
    }> {
        const [taxiStands, recentSearches, lastSync] = await Promise.all([
            this.getTaxiStands(),
            this.getRecentSearches(),
            this.getAppData('lastSync')
        ]);

        return {
            taxiStandsCount: taxiStands.length,
            recentSearchesCount: recentSearches.length,
            lastSync: lastSync || 0,
            isOnline: this.isOnline
        };
    }
}

// Global offline manager instance
export const offlineManager = new OfflineManager();

export default OfflineManager;
